import { BorderGridContainer, GridBox, HeroBanner } from '@ahmdigital/ui';
import { isEmpty } from 'lodash/fp';
import { useDispatch } from 'react-redux';
import React, { useEffect } from 'react';

import { ACQUISITION_PAGE_SLUGS, PAGE_TITLE_TEMPLATE } from '../../ahm-constants/ui';
import { VARIANT } from '../../components/offers/tile/constants';
import AccordionSection from '../../components/sections/accordion-section';
import ContentTilesSection from '../../components/content-tiles-section';
import CustomerActions from '../../actions/customer';
import FindCoverSection from '../media-landing/components/find-cover-section';
import getContentToRender from '../explained-term/detail/utils/get-content-to-render';
import getFormattedQuestionForAccordion from '../../utils/get-formatted-question-for-accordion';
import Head from '../../../utils/head';
import mediaLandingPageData from '../media-landing/data';
import MeetTheTeam from '../offers/components/meet-the-team';
import OfferSection from '../media-landing/components/offer-section';
import offersPageData from '../offers/data';
import OfferTile from '../../components/offers/tile';
import questionsHeroImage from '../../images/questions-hero.svg';
import ReferralActions from '../../actions/referral';
import ReferrerActions from '../../actions/referrer';
import type { ItemType } from '../home/components/content-tiles/utils/map-content';
import type { Offer, Placeholder } from '../../components/offers/types';
import type { ProductTypeType } from '../../types/product';

export type AcquisitionLandingPageProps = {
  activeOffers: Offer[];
  pageContent: {
    item: ItemType;
  }[];
  placeholders: Placeholder[];
  productType?: ProductTypeType;
  slug: string;
};

const AcquisitionLandingPage = ({
  activeOffers,
  pageContent,
  placeholders,
  productType,
  slug,
}: AcquisitionLandingPageProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(ReferralActions.reset());
    dispatch(ReferrerActions.setHasPartnerReferrer(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(CustomerActions.changeProductType(productType));
  }, [dispatch, productType]);

  const tableOfContents = pageContent?.map((entry) => entry.item);

  const { OFFERS_PAGE, MEDIA_LANDING_PAGE } = ACQUISITION_PAGE_SLUGS;

  const metaData =
    slug === OFFERS_PAGE
      ? {
          description: offersPageData.meta.description,
          title: offersPageData.meta.title,
        }
      : {
          description: mediaLandingPageData.meta.description,
          title: mediaLandingPageData.meta.title,
        };

  return (
    <>
      <Head titleTemplate={PAGE_TITLE_TEMPLATE}>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
      </Head>
      {!isEmpty(activeOffers) && slug === OFFERS_PAGE && (
        <BorderGridContainer hideBorderTop>
          <GridBox padding="0">
            <OfferTile
              placeholders={placeholders}
              overrideOffers={activeOffers}
              heading="Check out our offers"
              variant={VARIANT.HEADER}
              isBoxed={false}
            />
          </GridBox>
        </BorderGridContainer>
      )}
      {slug === MEDIA_LANDING_PAGE && (
        <>
          <HeroBanner {...mediaLandingPageData?.sections?.['hero-banner']} />
          <OfferSection offers={activeOffers} placeholders={placeholders} />
          <BorderGridContainer hideBorderTop>
            <GridBox padding="1rem">
              <FindCoverSection />
            </GridBox>
          </BorderGridContainer>
        </>
      )}
      {getContentToRender(tableOfContents)}
      {slug === OFFERS_PAGE && <MeetTheTeam />}
      {slug === MEDIA_LANDING_PAGE && (
        <>
          <ContentTilesSection contentTiles={mediaLandingPageData?.sections?.['content-tiles'].tiles} />
          <AccordionSection
            accordionId="explained-term-common-questions"
            image={{
              alt: 'Billboard stating "Got questions? We’ve got the answers"',
              src: questionsHeroImage.src,
            }}
            items={getFormattedQuestionForAccordion(mediaLandingPageData?.sections?.['common-questions'].questions)}
            title={mediaLandingPageData?.sections?.['common-questions'].title}
            withBorder
          />
        </>
      )}
    </>
  );
};

export default AcquisitionLandingPage;
